import { useCallback, useEffect, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import type { GoalConfig, Schedule } from "../../../shared/goals/config";
import { splitVariant, type Plan } from "../../../shared/goals/plan";
import { Icon, type IconName } from "../../components/ui/Icon";
import { Info } from "../../components/ui/Info";
import { ccApi, type Person, type ReadyLite, type Results } from "../../lib/ccApi";
import { paragraphs } from "../goal/blocks";
import { goalDate, initials, isoDay } from "../goal/text";
import { AnySheet } from "./AnySheet";
import { agentCents, dayLabel, fmt, hm, longDay, parseDay, parseTime, perResult, plural, shortDay, totalSpent, usd, usd0 } from "./text";
import { errText } from "./useCc";
import { Can, useCan } from "../../context/AuthContext";

// --- Settings: everything the owner tunes, applied as the next version ------------------------------------

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const at = (s: string) => s.replace(/^0(\d)/, "$1");
export function scheduleText(s: Schedule): string {
  if (s.kind === "daily") return `Every day at ${at(s.at)}`;
  if (s.kind === "weekly") return `Every ${WEEKDAYS[s.day]} at ${at(s.at)}`;
  if (s.minutes === 60) return "Every hour";
  if (s.minutes === 720) return "Twice a day";
  return s.minutes % 60 ? `Every ${s.minutes} minutes` : `Every ${s.minutes / 60} hours`;
}
const REPLIES = [60, 120, 240, 720];
const AD_DAYS = [2, 3, 5, 7];
const adDays = (n: number) => (n === 7 ? "At most once a week" : n === 1 ? "At most once a day" : n === 0 ? "Any time" : `At most every ${n} days`);
const sweepText = (s: Schedule) => (s.kind === "weekly" ? `Every ${WEEKDAYS[s.day]}` : s.kind === "daily" ? "Every day" : scheduleText(s));
const num = (v: string) => {
  const x = Number(String(v).replace(/[$,\s]/g, ""));
  return Number.isFinite(x) ? x : NaN;
};
const cents = (v: string) => Math.round(num(v) * 100);

/** The tools a goal may switch off, in the owner's words (the prototype's USE). */
const TOOLS: [string, string, string][] = [
  ["web_search", "Web search", "Search results, about $0.001 a search"],
  ["read_page", "Read a page", "People's own sites and company pages"],
  ["modelmatch_lookup", "Sales records", "Closed sales for one person, last 12 months"],
  ["call_catalog_tool", "Data sources", "Paid lookups, like checking an email works"],
  ["generate_ad_image", "Image maker", "Extra ideas only, never a person"],
];

type Field = { id: string; pre?: string; type?: "time" | "text"; ph?: string } | { id: string; options: [string, string][] };
interface Row {
  label: string;
  value: (c: GoalConfig) => string;
  fields: (c: GoalConfig) => (Field & { init: string })[];
  /** The next config and the version's line, an error to show, or null when nothing changed. */
  save: (c: GoalConfig, v: Record<string, string>, spentCents: number) => { next: GoalConfig; line: string } | string | null;
}

const ROWS: Record<string, Row> = {
  goal: {
    label: "Results wanted",
    value: (c) => `${fmt(c.result.target)}: ${c.result.label}`,
    fields: (c) => [{ id: "v", pre: "", init: String(c.result.target) }],
    save: (c, v) => {
      const n = Math.round(num(v.v));
      if (!(n > 0)) return "Add a number above 0.";
      if (n === c.result.target) return null;
      return { next: { ...c, result: { ...c.result, target: n } }, line: `Results wanted from ${fmt(c.result.target)} to ${fmt(n)}.` };
    },
  },
  budget: {
    label: "Budget",
    value: (c) => `${usd(c.money.budgetCents)} in total`,
    fields: (c) => [{ id: "v", pre: "$", init: String(c.money.budgetCents / 100) }],
    save: (c, v, spent) => {
      const n = cents(v.v);
      if (!(n >= spent) || !Number.isFinite(n)) return `It has to be at least the ${usd(spent)} already spent.`;
      if (n === c.money.budgetCents) return null;
      return { next: { ...c, money: { ...c.money, budgetCents: n } }, line: `Budget from ${usd(c.money.budgetCents)} to ${usd(n)}.` };
    },
  },
  daily: {
    label: "Daily budget",
    value: (c) => (c.money.dailyMaxCents ? `Up to ${usd(c.money.dailyMaxCents)} a day` : "No daily budget; the total sets the pace"),
    fields: (c) => [{ id: "v", pre: "$", init: c.money.dailyMaxCents ? String(c.money.dailyMaxCents / 100) : "" }],
    save: (c, v) => {
      const n = cents(v.v);
      if (!(n >= 500)) return "Use $5 a day or more.";
      if (n > c.money.budgetCents) return "The daily budget cannot be more than the whole budget.";
      if (n === c.money.dailyMaxCents) return null;
      return {
        next: { ...c, money: { ...c.money, dailyMaxCents: n } },
        line: c.money.dailyMaxCents ? `Daily budget from up to ${usd(c.money.dailyMaxCents)} to up to ${usd(n)} a day.` : `Daily budget set to up to ${usd(n)} a day.`,
      };
    },
  },
  max: {
    label: "Most per result",
    value: (c) => usd(c.money.perResultMaxCents),
    fields: (c) => [{ id: "v", pre: "$", init: String(c.money.perResultMaxCents / 100) }],
    save: (c, v) => {
      const n = cents(v.v);
      if (!(n > 0)) return "Add an amount above $0.";
      if (n === c.money.perResultMaxCents) return null;
      return { next: { ...c, money: { ...c.money, perResultMaxCents: n } }, line: `Most per result from ${usd(c.money.perResultMaxCents)} to ${usd(n)}.` };
    },
  },
  by: {
    label: "Deadline",
    value: (c) => longDay(parseDay(c.result.by)),
    fields: (c) => [{ id: "v", type: "text", ph: "For example 30 November", init: longDay(parseDay(c.result.by)) }],
    save: (c, v) => {
      const d = goalDate(`by ${v.v.trim()}`);
      if (!d) return "Write a date, like 30 November.";
      if (d <= new Date()) return "Pick a date after today.";
      const by = isoDay(d);
      if (by === c.result.by) return null;
      return { next: { ...c, result: { ...c.result, by } }, line: `Deadline from ${longDay(parseDay(c.result.by))} to ${longDay(d)}.` };
    },
  },
  stop: {
    label: "Stop rule",
    value: (c) => `Stop an ad at ${usd(c.rules.stopNoResultCents)} with no result`,
    fields: (c) => [{ id: "v", pre: "$", init: String(c.rules.stopNoResultCents / 100) }],
    save: (c, v) => {
      const n = cents(v.v);
      if (!(n > 0)) return "Add an amount above $0.";
      if (n === c.rules.stopNoResultCents) return null;
      return { next: { ...c, rules: { ...c.rules, stopNoResultCents: n } }, line: `Stop rule from ${usd(c.rules.stopNoResultCents)} to ${usd(n)} with no result.` };
    },
  },
  patience: {
    label: "Tests decided after",
    value: (c) => `${plural(c.rules.patience.days, "day")} and ${plural(c.rules.patience.clicks, "click")}`,
    fields: (c) => [
      { id: "d", pre: "Days", init: String(c.rules.patience.days) },
      { id: "k", pre: "Clicks", init: String(c.rules.patience.clicks) },
    ],
    save: (c, v) => {
      const d = Math.round(num(v.d));
      const k = Math.round(num(v.k));
      if (!(d > 0 && k > 0)) return "Add both numbers.";
      if (d === c.rules.patience.days && k === c.rules.patience.clicks) return null;
      return { next: { ...c, rules: { ...c.rules, patience: { days: d, clicks: k } } }, line: `Tests decided after ${plural(d, "day")} and ${plural(k, "click")}.` };
    },
  },
  review: {
    label: "Morning review",
    value: (c) => scheduleText(c.rhythm.review),
    fields: (c) => [{ id: "v", type: "time", init: c.rhythm.review.kind === "every" ? "09:00" : c.rhythm.review.at }],
    save: (c, v) => {
      if (!/^\d{2}:\d{2}$/.test(v.v)) return "Pick a time.";
      const r = c.rhythm.review;
      if (r.kind !== "every" && r.at === v.v) return null;
      const next: Schedule = r.kind === "weekly" ? { ...r, at: v.v } : { kind: "daily", at: v.v };
      return { next: { ...c, rhythm: { ...c.rhythm, review: next } }, line: `Morning review ${r.kind === "every" ? "" : `from ${at(r.at)} `}to ${at(v.v)}.` };
    },
  },
  replies: {
    label: "Reply checks",
    value: (c) => scheduleText(c.rhythm.replies),
    fields: (c) => {
      const cur = c.rhythm.replies.kind === "every" ? c.rhythm.replies.minutes : 120;
      const opts = [...new Set([...REPLIES, cur])].toSorted((a, b) => a - b);
      return [{ id: "v", init: String(cur), options: opts.map((m) => [String(m), scheduleText({ kind: "every", minutes: m })] as [string, string]) }];
    },
    save: (c, v) => {
      const m = Number(v.v);
      if (c.rhythm.replies.kind === "every" && c.rhythm.replies.minutes === m) return null;
      const t = scheduleText({ kind: "every", minutes: m });
      return { next: { ...c, rhythm: { ...c.rhythm, replies: { kind: "every", minutes: m } } }, line: `Reply checks to ${t.toLowerCase()}.` };
    },
  },
  adChange: {
    label: "Changing a live ad",
    value: (c) => adDays(c.rhythm.liveAdChangeDays),
    fields: (c) => {
      const opts = [...new Set([...AD_DAYS, c.rhythm.liveAdChangeDays])].toSorted((a, b) => a - b);
      return [{ id: "v", init: String(c.rhythm.liveAdChangeDays), options: opts.map((d) => [String(d), adDays(d)] as [string, string]) }];
    },
    save: (c, v) => {
      const d = Number(v.v);
      if (d === c.rhythm.liveAdChangeDays) return null;
      return { next: { ...c, rhythm: { ...c.rhythm, liveAdChangeDays: d } }, line: `Live ad edits to ${adDays(d).toLowerCase()}.` };
    },
  },
  sweep: {
    label: "Finding people",
    value: (c) => sweepText(c.rhythm.sweep),
    fields: (c) => [
      {
        id: "v",
        init: c.rhythm.sweep.kind === "daily" ? "daily" : c.rhythm.sweep.kind === "weekly" ? `w${c.rhythm.sweep.day}` : "every",
        options: [["daily", "Every day"], ...WEEKDAYS.map((d, i) => [`w${i}`, `Every ${d}`] as [string, string]), ...(c.rhythm.sweep.kind === "every" ? [["every", sweepText(c.rhythm.sweep)] as [string, string]] : [])],
      },
    ],
    save: (c, v) => {
      const s = c.rhythm.sweep;
      const time = s.kind === "every" ? "14:00" : s.at;
      const next: Schedule | null = v.v === "daily" ? { kind: "daily", at: time } : v.v.startsWith("w") ? { kind: "weekly", day: Number(v.v.slice(1)), at: time } : null;
      if (!next || JSON.stringify(next) === JSON.stringify(s)) return null;
      return { next: { ...c, rhythm: { ...c.rhythm, sweep: next } }, line: `Finding people to ${sweepText(next).toLowerCase()}.` };
    },
  },
  data: {
    label: "Data and AI",
    value: (c) => `Up to $${(c.caps.perRunMicros / 1e6).toFixed(2)} a task`,
    fields: (c) => [{ id: "v", pre: "$", init: (c.caps.perRunMicros / 1e6).toFixed(2) }],
    save: (c, v) => {
      const n = Math.round(num(v.v) * 1e6);
      if (!(n > 0)) return "Add an amount above $0.";
      if (n === c.caps.perRunMicros) return null;
      return { next: { ...c, caps: { ...c.caps, perRunMicros: n } }, line: `Most per run for data and AI to $${(n / 1e6).toFixed(2)}.` };
    },
  },
};

/** The rows that change the money. */
const MONEY_ROWS = new Set(["budget", "daily", "max"]);

/** Every setting a card shows, as one plain line: what a version's settings say. */
export const settingLines = (c: GoalConfig) => Object.values(ROWS).map((r) => `${r.label}: ${r.value(c)}`);

function SetRow({ k, config, editing, canEdit, onEdit, onCancel, onSave }: { k: string; config: GoalConfig; editing: boolean; canEdit: boolean; onEdit: () => void; onCancel: () => void; onSave: (v: Record<string, string>) => Promise<string | null> }) {
  const row = ROWS[k];
  const fields = row.fields(config);
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((f) => [f.id, f.init])));
  const [err, setErr] = useState("");
  const save = async () => setErr((await onSave(vals)) ?? "");
  const key = (e: KeyboardEvent) => {
    if (e.key === "Enter") void save();
    if (e.key === "Escape") onCancel();
  };
  if (!editing)
    return (
      <div className="gs-row" id={`gs-${k}`}>
        <span className="gs-l">{row.label}</span>
        <span className="gs-v">{row.value(config)}</span>
        {canEdit && (
          <button type="button" className="chip link gs-edit" onClick={onEdit} aria-label={`Edit ${row.label}`}>
            <Icon name="pen" />
            Edit
          </button>
        )}
      </div>
    );
  const input = (f: Field & { init: string }) => {
    const v = vals[f.id] ?? "";
    const set = (x: string) => setVals((o) => ({ ...o, [f.id]: x }));
    if ("options" in f)
      return (
        <select key={f.id} className="field gs-sel" aria-label={row.label} value={v} onChange={(e) => set(e.target.value)}>
          {f.options.map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </select>
      );
    if (f.type)
      return <input key={f.id} className="field gs-in" type={f.type} aria-label={row.label} placeholder={f.ph} value={v} onChange={(e) => set(e.target.value)} onKeyDown={key} autoFocus />;
    return (
      <div className="money gs-num" key={f.id}>
        {f.pre ? <span>{f.pre}</span> : null}
        <input aria-label={f.pre && f.pre !== "$" ? f.pre : row.label} inputMode="decimal" value={v} onChange={(e) => set(e.target.value)} onKeyDown={key} autoFocus={f.id !== "k"} />
      </div>
    );
  };
  return (
    <div className="gs-row on" id={`gs-${k}`}>
      <span className="gs-l">{row.label}</span>
      <div className="gs-f">
        {fields.length > 1 ? <div className="row2">{fields.map(input)}</div> : fields.map(input)}
        <span className="gs-err" role="alert">
          {err}
        </span>
        <div className="m-act">
          <button type="button" className="btn sm gs-save" onClick={() => void save()}>
            Save
          </button>
          <button type="button" className="btn sm ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface SettingsProps {
  goalId: string;
  config: GoalConfig;
  version: number;
  state: string;
  spentCents: number;
  editHref: string;
  say: (m: string, ok?: boolean) => void;
  onSaved: () => Promise<void>;
  onPause: (on: boolean) => void;
}

/** The goal's settings (the prototype's settings card): only the owner changes them; each change is a new version. */
export function SettingsCard({ goalId, config, version, state, spentCents, editHref, say, onSaved, onPause }: SettingsProps) {
  const [editing, setEditing] = useState<string | null>(null);
  const [calling, setCalling] = useState(false);
  const can = useCan();
  const running = (state === "live" || state === "paused") && can("editor");

  const apply = async (next: GoalConfig, line: string): Promise<string | null> => {
    try {
      const r = await ccApi.apply(goalId, version, next, line);
      setEditing(null);
      say(`Saved as plan version ${r.version}. The agent follows it from the next review.`);
      await onSaved();
      return null;
    } catch (e) {
      return errText(e);
    }
  };
  const save = (k: string) => async (v: Record<string, string>) => {
    const r = ROWS[k].save(config, v, spentCents);
    if (r === null) {
      setEditing(null);
      say("Nothing changed.");
      return null;
    }
    if (typeof r === "string") return r;
    return apply(r.next, r.line);
  };
  // Why: an approved goal's budget is an Admin's; the server refuses it from anyone else.
  const row = (k: string) => (
    <SetRow key={`${k}:${editing === k}`} k={k} config={config} editing={editing === k} canEdit={running && (!MONEY_ROWS.has(k) || can("admin"))} onEdit={() => setEditing(k)} onCancel={() => setEditing(null)} onSave={save(k)} />
  );
  const tool = async (id: string, label: string, on: boolean) => {
    const err = await apply({ ...config, tools: { ...config.tools, [id]: on } }, `${label} switched ${on ? "on" : "off"}.`);
    if (err) say(err, false);
  };
  const setCallingOn = async (on: boolean) => {
    setCalling(false);
    const err = await apply({ ...config, rules: { ...config.rules, callingAndTexting: on } }, on ? "Calling and texting turned on." : "Calling and texting turned off.");
    if (err) say(err, false);
  };

  return (
    <div className="gs-grid">
      <section className="card gs-card">
        <div className="card-h">
          <h3 className="h-i">
            Goal<Info label="Who changes it">Only you change these. The agent works inside them and cannot change them itself.</Info>
          </h3>
        </div>
        {["goal", "budget", "daily", "max", "by"].map(row)}
      </section>
      <section className="card gs-card">
        <div className="card-h">
          <h3 className="h-i">
            Your rules<Info label="Who checks">Checked by Flashadz before every action, not by the AI.</Info>
          </h3>
        </div>
        {["stop", "patience"].map(row)}
        <div className="gs-row">
          <span className="gs-l">Calling and texting</span>
          <span className="gs-v">{config.rules.callingAndTexting ? "On. Each call and text asks you first." : "Off until you turn them on. Collecting numbers is fine."}</span>
          {running && (
            <label className="tg gs-sw">
              <input
                type="checkbox"
                checked={config.rules.callingAndTexting}
                aria-label="Calling and texting"
                onChange={(e) => (e.target.checked ? setCalling(true) : void setCallingOn(false))}
              />
              <span className="sw" />
            </label>
          )}
        </div>
      </section>
      <section className="card gs-card">
        <div className="card-h">
          <h3>Rhythm</h3>
        </div>
        {["review", "replies", "adChange", "sweep"].map(row)}
      </section>
      <section className="card gs-card">
        <div className="card-h">
          <h3 className="h-i">
            What the agent may use<Info label="How it works">Switch a tool off and the agent works without it. Anything that spends or speaks for you still asks you first.</Info>
          </h3>
        </div>
        {TOOLS.map(([id, n, d]) => (
          <div className="gs-row gs-use" key={id}>
            <span className="gs-l">{n}</span>
            <span className="gs-v">{d}</span>
            {running && (
              <label className="tg gs-sw">
                <input type="checkbox" checked={config.tools[id] !== false} aria-label={n} onChange={(e) => void tool(id, n, e.target.checked)} />
                <span className="sw" />
              </label>
            )}
          </div>
        ))}
        {row("data")}
        <div className="gs-never">
          <span className="gs-l">Never</span>
          <span className="gs-v">{config.rules.never.join(". ")}.</span>
        </div>
      </section>
      {running && (
        <p className="ch-off gs-full">
          Channels, the playbook and the skills change in{" "}
          <Link className="ch-lnk" to={editHref}>
            Edit goal
          </Link>
          .
        </p>
      )}
      {running && can(state === "paused" ? "admin" : "editor") && (
        <section className="card kill gs-full">
          <h3>{state === "paused" ? "Everything is paused" : "Pause everything"}</h3>
          <p>{state === "paused" ? "The ads are off and nothing sends. The agent still reads results." : "Turns the ads off and stops all emails and replies now. The agent keeps reading results."}</p>
          <button type="button" className={state === "paused" ? "btn" : "btn warn"} onClick={() => onPause(state !== "paused")}>
            <Icon name={state === "paused" ? "play" : "pause"} />
            {state === "paused" ? "Resume" : "Pause everything"}
          </button>
        </section>
      )}
      <AnySheet open={calling} onClose={() => setCalling(false)} label="Calling and texting">
        <div className="eyebrow">Your rules</div>
        <h2 style={{ marginTop: 6 }}>Turn on calling and texting?</h2>
        <p className="lede" style={{ fontSize: "var(--fs-text)" }}>
          The agent could then suggest a call or a text to people who gave their number. Each one would still wait for your OK, and cold email stays the first step.
        </p>
        <div className="m-act" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="btn sm ghost gs-keepoff"
            onClick={() => {
              setCalling(false);
              say("Calling and texting stay off.");
            }}
          >
            Keep them off
          </button>
          <button type="button" className="btn sm" onClick={() => void setCallingOn(true)}>
            Turn them on
          </button>
        </div>
      </AnySheet>
    </div>
  );
}

// --- Money: spend against the budget, the daily amount, cost per result by week -------------------------------

const each = perResult;
// Why: results carry an ad's own state (live, paused, stopped...); Get ready's view carries its step (launched, check...).
const AD_PILL: Record<string, [string, string]> = {
  live: ["live", "Running"],
  paused: ["tight", "Paused"],
  stopped: ["plain", "Stopped by your rule"],
  waiting: ["tight", "Waiting for you"],
  launched: ["live", "Running"],
  approved: ["good", "Approved"],
  check: ["tight", "Waiting for you"],
  needs_video: ["tight", "Needs its video"],
  drafting: ["live", "Drafting"],
  removed: ["plain", "Removed"],
};
const adPill = (status: string | null, review: string | null): [string, string] =>
  review === "DISAPPROVED" ? ["bad", "Rejected by Meta"] : (AD_PILL[status ?? ""] ?? ["plain", status ? status.replace(/_/g, " ") : "Outside Flashadz"]);

const asOf = (s: string | null) => (s ? `${shortDay(parseTime(s))} ${hm(parseTime(s))}` : "not read yet");

export function MoneyCard({ config, results }: { config: GoalConfig; results: Results }) {
  const t = results.totals;
  const agent = agentCents(results);
  const spent = totalSpent(results);
  const budget = config.money.budgetCents;
  const left = Math.max(0, budget - spent);
  const max = config.money.perResultMaxCents;
  const dailyMax = config.money.dailyMaxCents;
  const today = results.campaign?.dailyCents ?? null;
  const days = results.days.toSorted((a, b) => a.day.localeCompare(b.day));
  const weeks: { spend: number; regs: number }[] = [];
  for (let end = days.length; end > 0 && weeks.length < 4; end -= 7) {
    const w = days.slice(Math.max(0, end - 7), end);
    weeks.unshift({ spend: w.reduce((a, d) => a + d.spendCents, 0), regs: w.reduce((a, d) => a + d.results, 0) });
  }
  const costs = weeks.map((w) => (w.regs ? w.spend / w.regs : 0));
  const top = Math.max(max * 1.33, ...costs, 1);
  const since = days[0] ? `Since ${shortDay(parseDay(days[0].day))}` : "No spend yet";
  const pct = (c: number) => `${budget ? Math.min(100, (c / budget) * 100) : 0}%`;
  const mail = results.email;
  return (
    <>
      <div className="sgrid">
        <section className="card">
          <div className="card-h">
            <h3>Money</h3>
            <span className="sub">{since}</span>
          </div>
          <div className="big">
            {usd0(spent)} <small>of {usd(budget)}</small>
          </div>
          <div className="meter">
            <i style={{ width: pct(t.spendCents), background: "var(--fire)" }} />
            <i style={{ width: pct(agent), background: "var(--warn)" }} />
          </div>
          <div className="legend">
            <div className="lg">
              <span className="d" style={{ background: "var(--fire)" }} />
              <span>
                Meta ads<em>{results.campaign ? "One campaign, one ad set" : plural(results.ads.length, "ad")}</em>
              </span>
              <b>{usd0(t.spendCents)}</b>
            </div>
            <div className="lg">
              <span className="d" style={{ background: "var(--warn)" }} />
              <span>
                AI and data<em>The agent's {plural(results.agentCosts.runs, "run")}: research, writing and media</em>
              </span>
              <b>{usd0(agent)}</b>
            </div>
            <div className="lg">
              <span className="d" style={{ background: "var(--raise)" }} />
              <span>
                Left<em>Lasts to {longDay(parseDay(config.result.by))}{dailyMax ? ` at up to ${usd(dailyMax)} a day` : ""}</em>
              </span>
              <b>{usd0(left)}</b>
            </div>
          </div>
        </section>
        <div className="gx-col">
          <section className="card">
            <div className="card-h">
              <h3>Daily budget</h3>
              <span className="sub">The ad set</span>
            </div>
            <div className="gx-daily">
              <div className="gx-dh">
                <span>Today</span>
                <b>{today !== null ? `${usd(today)}${dailyMax ? ` of up to ${usd(dailyMax)}` : ""} a day` : results.campaign ? "Not set" : "No campaign yet"}</b>
              </div>
              {today !== null && dailyMax ? (
                <div className="pbar">
                  <i style={{ width: `${Math.min(100, (today / dailyMax) * 100)}%` }} />
                </div>
              ) : null}
              <p className="h-i">
                It rises about {config.rules.stepUpPct}% at a time while each result costs under {usd(max)}
                {dailyMax ? `, up to ${usd(dailyMax)} a day` : ""}.
                <Info label="How it rises">Big jumps start Meta's learning again, so the daily budget moves in small steps.</Info>
              </p>
            </div>
          </section>
          <section className="card">
            <div className="card-h">
              <h3>Cost per result</h3>
              <span className="sub">By week, ads only</span>
            </div>
            <div className="bars">
              <div className="gl" style={{ bottom: `calc(26px + (100% - 26px) * ${Math.min(1, max / top)})` }}>
                <span>Your most {usd(max)}</span>
              </div>
              {weeks.map((w, i) => (
                <div className={`b${i < weeks.length - 2 ? " old" : ""}`} key={i}>
                  <b>{perResult(w.spend, w.regs)}</b>
                  <i style={{ height: `${(costs[i] / top) * 100}%` }} />
                  <em>
                    W{i + 1} · {w.regs}
                    <span className="gx-wk"> {config.result.label}</span>
                  </em>
                </div>
              ))}
            </div>
          </section>
        </div>
        {config.channels.email?.on && (
          <section className="card gx-full">
            <div className="card-h">
              <h3>Cold email</h3>
              <span className="sub">{plural(mail.inCampaign, "person", "people")} in the campaign</span>
            </div>
            <div className="gx-res">
              <div>
                <b>{fmt(mail.sent)}</b>
                <span>emails sent</span>
              </div>
              <div>
                <b>{fmt(mail.replies)}</b>
                <span>replies</span>
              </div>
              <div>
                <b>{fmt(mail.bounced)}</b>
                <span>didn't arrive</span>
              </div>
              <div>
                <b>{fmt(mail.optedOut)}</b>
                <span>unsubscribed</span>
              </div>
            </div>
          </section>
        )}
      </div>
      <p className="cc-fine">
        Meta read {asOf(results.sources.meta.asOf)}
        {results.sources.meta.error ? ` (${results.sources.meta.error})` : ""}
      </p>
    </>
  );
}

// --- Tests: the plan's tests, each inside the one ad set, with their variants' results -----------------------

export function TestsCard({ plan, results, config }: { plan: Plan | null; results: Results | null; config: GoalConfig | null }) {
  if (!plan?.tests.length) return <p className="cc-fine">The plan has no tests yet.</p>;
  const patience = config?.rules.patience.clicks ?? 0;
  return (
    <>
      <div className="gx-h">
        <h3 className="h-i">
          Tests<Info label="How tests run">Tests compare ad ideas inside the one ad set, on the same daily budget. No test has a budget of its own.</Info>
        </h3>
      </div>
      <div className="tgrid">
        {plan.tests.map((t) => {
          const r = results?.tests.find((x) => x.id === t.id);
          const variants = r?.variants ?? [];
          const started = variants.some((v) => v.adIds.length);
          const clicks = variants.reduce((a, v) => a + v.clicks, 0);
          const ready = started && patience > 0 && clicks >= patience;
          const pill = !started ? ["plain", "Not started"] : ready ? ["good", "Ready to decide"] : ["live", "Running"];
          const best = ready ? variants.toSorted((a, b) => b.results - a.results)[0] : undefined;
          return (
            <article className="test" key={t.id}>
              <div className="t-top">
                <span className="tid">{t.id}</span>
                <span className={`pill ${pill[0]}`}>{pill[1]}</span>
              </div>
              <h4>{t.name}</h4>
              <div className="t-sub">{t.hypothesis}</div>
              <div className="arms">
                {t.variants.map((name, j) => {
                  const v = variants.find((x) => x.variant === name);
                  const am = !v?.adIds.length ? "not started" : v.results ? `${v.results} · ${each(v.spendCents, v.results)} each` : `${fmt(v.clicks)} clicks`;
                  return (
                    <div className={`arm${best && best.variant === name && best.results ? " w" : ""}`} key={j}>
                      <span className="al">{String.fromCharCode(65 + j)}</span>
                      <span className="an">{splitVariant(name).text || name}</span>
                      <span className="am mono">{am}</span>
                    </div>
                  );
                })}
              </div>
              {started && patience > 0 ? (
                <div className="prog">
                  <div className="pbar">
                    <i style={{ width: `${Math.min(100, (clicks / patience) * 100)}%` }} />
                  </div>
                  <span>
                    {fmt(Math.min(clicks, patience))} of {fmt(patience)} clicks before it is decided
                  </span>
                </div>
              ) : (
                <div className="prog">
                  <span>
                    Decides on {t.metric.replace(/^./, (c) => c.toLowerCase())} · needs {(paragraphs(t.min_data)[0] ?? "").replace(/^./, (c) => c.toLowerCase())}
                  </span>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </>
  );
}

// --- Ads and emails, as Get ready holds them ------------------------------------------------------------

const words = (w: Record<string, unknown>, k: string) => (typeof w[k] === "string" ? (w[k] as string) : "");

export function ReadyCard({ goalId, ready }: { goalId: string; ready: ReadyLite }) {
  return (
    <section className="card">
      <div className="card-h">
        <h3>Ads and emails</h3>
      </div>
      {ready.ads.length > 0 ? (
        <div className="cc-ideas">
          {ready.ads.map((a) => {
            const [cls, label] = adPill(a.status, null);
            const chips = Array.isArray(a.words.chips) ? (a.words.chips as string[]) : [];
            return (
              <div className="cc-idea" key={a.id}>
                <div className="rd-ad-h" style={{ margin: 0 }}>
                  <b>{words(a.words, "name") || "Your idea"}</b>
                  <span className={`pill ${cls}`}>{label}</span>
                </div>
                {chips.length > 0 && <p>{chips.join(" · ")}</p>}
                {words(a.words, "primary") && <p>{words(a.words, "primary")}</p>}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="cc-fine">No ads yet.</p>
      )}
      {ready.emailOn && ready.emails.length > 0 && (
        <div className="cc-steps" style={{ marginTop: 10 }}>
          {ready.emails.map((e) => (
            <div className="cc-step" key={e.step}>
              <b>
                Email {e.step}: {e.subject}
              </b>
              <span className={`pill ${e.approved ? "good" : "tight"}`}>{e.approved ? "Approved" : "Waiting for you"}</span>
            </div>
          ))}
        </div>
      )}
      <Can min="editor">
        <div className="m-act">
          <Link className="btn sm ghost" to={`/goals/${encodeURIComponent(goalId)}/ready`}>
            Open Get ready
            <Icon name="right" />
          </Link>
        </div>
      </Can>
    </section>
  );
}

// --- People: everyone the goal found, emailed or signed up, and how each was found ----------------------------

const STAGES: [string, string][] = [
  ["", "All"],
  ["registered", "Registered"],
  ["talking", "Talking"],
  ["emailed", "Emailed"],
  ["found", "Found"],
];
const STAGE_PILL: Record<string, [string, string]> = {
  registered: ["st-reg", "Registered"],
  talking: ["st-talk", "Talking"],
  emailed: ["st-mail", "Emailed"],
  found: ["st-found", "Found"],
  opted_out: ["st-mail", "Unsubscribed"],
  bounced: ["st-mail", "Didn't arrive"],
};
const STAGE_RANK: Record<string, number> = { registered: 0, talking: 1, emailed: 2, found: 3 };
const hostOf = (u: string) => u.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
const dayOf = (t: string | null) => (t ? shortDay(parseTime(t)) : "");
const factDay = (s: string) => (/^\d{4}-\d{2}-\d{2}$/.test(s) ? shortDay(parseDay(s)) : s);
const touchOf = (p: Person) => p.emails.lastAt ?? p.updatedAt;
/** The last touch as the prototype writes it: Today, Yesterday, a weekday within the week, then the date. */
function touchDay(t: string, now = new Date()): string {
  const d = parseTime(t);
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 864e5);
  if (days < 2) return dayLabel(d, now);
  return days < 7 ? dayLabel(d, now).split(" ")[0] : shortDay(d);
}
/** Where a person came from, and how far the emails got. */
function cameFrom(p: Person): [string, string] {
  const src = p.email?.page ? hostOf(p.email.page) : p.facts[0] ? hostOf(p.facts[0].source) : (p.market ?? "");
  if (p.emails.replies) return [src, p.stage === "registered" ? "Replied, then registered" : "Replied"];
  if (p.emails.sent) return [src, p.emails.sent === 1 ? "First email sent" : `${p.emails.sent} emails sent`];
  return [src, p.email ? "Email they published" : "No email published"];
}

function Face({ name, big = false }: { name: string; big?: boolean }) {
  return (
    <i className={big ? "cc-face big" : "cc-face"} aria-hidden="true">
      {initials(name)}
    </i>
  );
}

const step = (key: string, icon: IconName, title: string, text: string, src?: string | null) => (
  <div className="rs" key={key}>
    <span className="rs-ic">
      <Icon name={icon} />
    </span>
    <div>
      <b>{title}</b>
      <p>{text}</p>
      {src ? <span className="chip">{src}</span> : null}
    </div>
  </div>
);

/** How the agent found one person: every fact with its source, the address and how it was checked (the receipt). */
function Receipt({ goalId, p }: { goalId: string; p: Person }) {
  const [, stage] = STAGE_PILL[p.stage] ?? ["", p.stage];
  return (
    <>
      <div className="rc-h">
        <Face name={p.name} big />
        <div>
          <h2>{p.name}</h2>
          <span>{[p.org, stage].filter(Boolean).join(" · ")}</span>
        </div>
      </div>
      <div className="eyebrow" style={{ marginTop: 20 }}>
        How the agent found them
      </div>
      <div className="rsteps">
        {p.facts.map((f, i) => step(`f${i}`, "doc", f.fact, `${hostOf(f.source)}${f.date ? ` · ${factDay(f.date)}` : ""}`))}
        {p.firstLine && step("fl", "reply", "What they wrote", `“${p.firstLine.text}”`, p.firstLine.source ? hostOf(p.firstLine.source) : null)}
        {p.email
          ? step(
              "em",
              "mail",
              "Email",
              `${p.email.address}${p.email.page ? `, published on ${hostOf(p.email.page)}` : ""}${p.email.seen ? ` (seen ${factDay(p.email.seen)})` : ""}.`,
              p.email.verification ? `Checked: ${p.email.verification.result}` : p.email.checked ? `Checked ${factDay(p.email.checked)}` : null,
            )
          : step("em", "mail", "Email", "None published, so they are left out of the emails.")}
        {p.phone && step("ph", "user", "Phone", p.phone.number, p.phone.source ? hostOf(p.phone.source) : null)}
        {(p.emails.sent > 0 || p.emails.replies > 0) &&
          step("ms", "reply", "Emails", `${plural(p.emails.sent, "email")} sent, ${plural(p.emails.replies, "reply", "replies")}${p.emails.lastAt ? `, last ${dayOf(p.emails.lastAt)}` : ""}.`)}
        {p.notes && step("ti", "flask", "Notes", p.notes)}
      </div>
      <div className="rc-f">
        <span className="mono">
          Found {dayOf(p.createdAt)}
          {p.market ? ` · ${p.market}` : ""}
        </span>
        {p.foundIn && (
          <Link className="chip link" to={`/goals/${encodeURIComponent(goalId)}/log?run=${encodeURIComponent(p.foundIn)}`}>
            Open in the log
            <Icon name="right" />
          </Link>
        )}
      </div>
    </>
  );
}

/**
 * A person's receipt in a sheet over the whole page. Why: inside a chat message the sheet would sit in the
 * message's box, so it is drawn at the Command Center's root (`root`, found from the part that opens it).
 */
function ReceiptSheet({ goalId, person, root, onClose }: { goalId: string; person: Person | null; root: Element | null; onClose: () => void }) {
  const sheet = (
    <AnySheet open={!!person} onClose={onClose} label={person ? `How the agent found ${person.name}` : "A person"}>
      {person && <Receipt goalId={goalId} p={person} />}
    </AnySheet>
  );
  return root ? createPortal(sheet, root) : sheet;
}

/** The Command Center root an element sits in, kept once the element mounts. */
function useRoot(): [Element | null, (el: HTMLElement | null) => void] {
  const [root, setRoot] = useState<Element | null>(null);
  const ref = useCallback((el: HTMLElement | null) => {
    if (el) setRoot(el.closest(".ga.ccx"));
  }, []);
  return [root, ref];
}

/** A message's `people` block: each row opens how the agent found that person (`GET /people?ids=`). */
export function PeopleRows({ goalId, rows, footnote, say }: { goalId: string; rows: { personId: string; name: string; line: string; when: string }[]; footnote?: string; say: (m: string, ok?: boolean) => void }) {
  const [root, ref] = useRoot();
  const [open, setOpen] = useState<Person | null>(null);
  const [known, setKnown] = useState<Map<string, Person> | null>(null);
  const show = async (id: string) => {
    let map = known;
    if (!map) {
      try {
        const r = await ccApi.people(goalId, { ids: rows.map((x) => x.personId) });
        map = new Map(r.people.map((p) => [p.id, p]));
        setKnown(map);
      } catch (e) {
        say(errText(e), false);
        return;
      }
    }
    const p = map.get(id);
    if (p) setOpen(p);
    else say("This person is no longer in this goal's list.", false);
  };
  return (
    <div className="cc-card" ref={ref}>
      <div className="cc-ppl">
        {rows.map((r) => (
          <button type="button" className="cc-pp" key={r.personId} onClick={() => void show(r.personId)}>
            <Face name={r.name} />
            <div>
              <b>{r.name}</b>
              <span>{r.line}</span>
            </div>
            <em>{r.when}</em>
          </button>
        ))}
      </div>
      {footnote && <p className="cc-fine">{footnote}</p>}
      <ReceiptSheet goalId={goalId} person={open} root={root} onClose={() => setOpen(null)} />
    </div>
  );
}

export function PeopleCard({ goalId }: { goalId: string }) {
  const [stage, setStage] = useState("");
  const [data, setData] = useState<{ stages: Record<string, number>; people: Person[] } | null>(null);
  const [all, setAll] = useState<Record<string, number>>({});
  const [open, setOpen] = useState<Person | null>(null);
  const [root, ref] = useRoot();
  useEffect(() => {
    let live = true;
    ccApi.people(goalId, { stage: stage || undefined, limit: 200 }).then(
      (r) => {
        if (!live) return;
        setData(r);
        if (!stage) setAll(r.stages);
      },
      () => live && setData({ stages: {}, people: [] }),
    );
    return () => {
      live = false;
    };
  }, [goalId, stage]);
  const total = Object.values(all).reduce((a, n) => a + n, 0);
  const count = (k: string) => (k ? (all[k] ?? 0) : total);
  const rows = (data?.people ?? []).toSorted((a, b) => (STAGE_RANK[a.stage] ?? 4) - (STAGE_RANK[b.stage] ?? 4) || touchOf(b).localeCompare(touchOf(a)));
  return (
    <>
      <div className="fchips" ref={ref}>
        {STAGES.map(([k, l]) => (
          <button type="button" key={k || "all"} className={stage === k ? "on" : ""} onClick={() => setStage(k)}>
            {l}
            <span>{count(k)}</span>
          </button>
        ))}
      </div>
      <div className="card ltable" style={{ padding: "4px 0" }}>
        <div className="lrow hd">
          <span>Person</span>
          <span>Stage</span>
          <span>Came from</span>
          <span style={{ textAlign: "right" }}>Last touch</span>
        </div>
        {rows.map((p) => {
          const [cls, label] = STAGE_PILL[p.stage] ?? ["st-mail", p.stage];
          const [src, how] = cameFrom(p);
          return (
            <div className="lrow tap" key={p.id} role="button" tabIndex={0} onClick={() => setOpen(p)} onKeyDown={(e) => e.key === "Enter" && setOpen(p)}>
              <div className="who c-who">
                <Face name={p.name} />
                <div style={{ minWidth: 0 }}>
                  <b>
                    {p.name}
                    {p.tier && (
                      <span className="tier" style={{ display: "inline" }}>
                        {p.tier}
                      </span>
                    )}
                  </b>
                  <span>{p.org ?? p.role ?? ""}</span>
                </div>
              </div>
              <div className="c-st">
                <span className={`st-pill ${cls}`}>{label}</span>
              </div>
              <div className="c-src">
                {src}
                <small>{how}</small>
              </div>
              <div className="c-when">{touchDay(touchOf(p))}</div>
            </div>
          );
        })}
        {data && !rows.length && <p className="cc-fine" style={{ padding: "0 18px 12px" }}>Nobody here yet.</p>}
      </div>
      <p className="fine" style={{ textAlign: "left", marginTop: 12 }}>
        Showing {rows.length} of {count(stage)}. Tap a person to see how they were found.
      </p>
      <ReceiptSheet goalId={goalId} person={open} root={root} onClose={() => setOpen(null)} />
    </>
  );
}
